import { Avatar, Col, Row, Spinner2, Text } from 'components';
import { Api } from 'lib';
import { useCallback, useEffect, useState } from 'react';
import { Header, TableComponents } from 'screens/Admin/components';
import StripeRequestPanel from './components/StripeRequestPanel';

const STATUS_OPTIONS = ['All', 'pending', 'approved', 'rejected', 'completed'];
const TYPE_OPTIONS = ['All', 'replacement', 'deletion'];

const statusColors = {
  pending: 'bg-[#FEF9C3] text-[#854D0E]',
  approved: 'bg-[#DBEAFE] text-[#1E40AF]',
  rejected: 'bg-[#FEE2E2] text-[#991B1B]',
  completed: 'bg-[#D1FAE5] text-[#065F46]'
};

const migrationColors = {
  queued: 'bg-[#FEF9C3] text-[#854D0E]',
  processing: 'bg-[#DBEAFE] text-[#1E40AF]',
  completed: 'bg-[#D1FAE5] text-[#065F46]',
  failed: 'bg-[#FEE2E2] text-[#991B1B]'
};

const formatLabel = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : '-';

const getMigrationStatus = (request) => {
  if (!request || request.type !== 'replacement' || request.status !== 'completed') {
    return null;
  }

  if (request.migrationStatus) {
    return request.migrationStatus;
  }

  return request.subscriptionsMigrated ? 'completed' : 'queued';
};

const shouldPollMigration = (request) => {
  const status = getMigrationStatus(request);
  return status === 'queued' || status === 'processing';
};

const StatusBadge = ({ status }) => (
  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${statusColors[status] || 'bg-[#F3F4F6] text-[#374151]'}`}>
    {formatLabel(status)}
  </span>
);

const AdminStripeRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const limit = 20;

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'All') params.set('status', statusFilter);
      if (typeFilter !== 'All') params.set('type', typeFilter);
      if (skip) params.set('skip', String(skip));
      params.set('limit', String(limit));

      const query = params.toString();
      const res = await Api.get(`/admin/teams/stripe/stripeRequests?${query}`);
      const data = res?.data || {};
      const nextRequests = data.requests || [];
      setRequests(nextRequests);
      setTotal(data.pagination?.total || 0);
      setSelectedRequest((prev) => {
        if (!prev) return null;
        return nextRequests.find((req) => req._id === prev._id) || prev;
      });
    } catch (err) {
      console.error('Error fetching stripe requests', err);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, skip]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    if (!selectedRequest || !shouldPollMigration(selectedRequest)) {
      return undefined;
    }

    const pollInterval = setInterval(() => {
      fetchRequests();
    }, 12000);

    return () => clearInterval(pollInterval);
  }, [fetchRequests, selectedRequest]);

  function handleFilterChange() {
    setSkip(0);
  }

  function handleStatusFilter(val) {
    setStatusFilter(val);
    handleFilterChange();
  }

  function handleTypeFilter(val) {
    setTypeFilter(val);
    handleFilterChange();
  }

  function prevPage() {
    setSkip(Math.max(0, skip - limit));
  }

  function nextPage() {
    if (skip + limit < total) setSkip(skip + limit);
  }

  function handleActionDone(options = { closePanel: true }) {
    if (options.closePanel !== false) {
      setSelectedRequest(null);
    }
    fetchRequests();
  }

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row center between wid="100%">
          <Text h3>Stripe Account Requests</Text>
          <Text b2 col="#6B7280">{total} total</Text>
        </Row>
      </Header>

      <Col start noFlex marg="54px 0 0" wid="100%" ht="100vh" scrollY>
        {/* Filters */}
        <div className="flex items-center gap-3 px-6 py-3 border-b bg-[#FAFBFC] flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-[12px] text-[#6B7280] font-medium">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm cursor-pointer bg-white"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{formatLabel(s)}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-[12px] text-[#6B7280] font-medium">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => handleTypeFilter(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm cursor-pointer bg-white"
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>{formatLabel(t)}</option>
              ))}
            </select>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-[#5F6F86]">
              {total === 0 ? '0' : `${Math.min(total, skip + 1)}–${Math.min(total, skip + limit)}`} of {total}
            </span>
            <button
              onClick={prevPage}
              disabled={skip === 0}
              className="px-3 py-1 border rounded-md cursor-pointer disabled:opacity-40 text-sm"
            >
              Prev
            </button>
            <button
              onClick={nextPage}
              disabled={skip + limit >= total}
              className="px-3 py-1 border rounded-md cursor-pointer disabled:opacity-40 text-sm"
            >
              Next
            </button>
          </div>
        </div>

        {loading && (
          <Col wid="100%" marg="40px 0" centerAll noFlex>
            <Spinner2 />
          </Col>
        )}

        {!loading && requests.length === 0 && (
          <Col wid="100%" centerAll noFlex marg="40px 0">
            <Text col="#9CA3AF">No Stripe account requests found</Text>
          </Col>
        )}

        {!loading && requests.length > 0 && (
          <TableComponents.List
            loading={loading}
            Header={() => (
              <>
                <TableComponents.Th first><Text>Team</Text></TableComponents.Th>
                <TableComponents.Th><Text>Type</Text></TableComponents.Th>
                <TableComponents.Th><Text>Status</Text></TableComponents.Th>
                <TableComponents.Th><Text>Reason</Text></TableComponents.Th>
                <TableComponents.Th last><Text>Requested At</Text></TableComponents.Th>
              </>
            )}
            Items={() =>
              requests.map((r) => {
                const migrationStatus = getMigrationStatus(r);
                return (
                  <TableComponents.Tr key={r._id} onClick={() => setSelectedRequest(r)}>
                    <TableComponents.Td first>
                      <div className="flex items-center gap-2">
                        <Avatar user={r.teamId} avatar={r.teamId?.avatar} tiny />
                        <div className="flex flex-col">
                          <Text>{r.teamId?.name || r.teamId?.username || '-'}</Text>
                          <Text col="#6B7280" style={{ fontSize: 11 }}>{r.teamId?.email || ''}</Text>
                        </div>
                      </div>
                    </TableComponents.Td>
                    <TableComponents.Td>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${r.type === 'replacement' ? 'bg-[#EDE9FE] text-[#5B21B6]' : 'bg-[#FEE2E2] text-[#991B1B]'}`}>
                        {formatLabel(r.type)}
                      </span>
                    </TableComponents.Td>
                    <TableComponents.Td>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <StatusBadge status={r.status} />
                        {migrationStatus && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${migrationColors[migrationStatus] || 'bg-[#F3F4F6] text-[#374151]'}`}>
                            Migration {formatLabel(migrationStatus)}
                          </span>
                        )}
                      </div>
                    </TableComponents.Td>
                    <TableComponents.Td>
                      <Text col="#6B7280" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.reason || '-'}
                      </Text>
                    </TableComponents.Td>
                    <TableComponents.Td last>
                      <Text>{r.requestedAt ? new Date(r.requestedAt).toLocaleString() : '-'}</Text>
                    </TableComponents.Td>
                  </TableComponents.Tr>
                );
              })
            }
          />
        )}
      </Col>

      <StripeRequestPanel
        request={selectedRequest}
        visible={!!selectedRequest}
        closePanel={() => setSelectedRequest(null)}
        onActionDone={handleActionDone}
      />
    </Col>
  );
};

export default AdminStripeRequests;
